import { useState } from 'react'
import { diagnosticBundleFilename, isRole, roleHasCapability, summarizeBundle } from '@qim/shared'
import { ApiError, fetchDiagnosticBundle } from '../api.js'
import type { JSX } from 'react'

/**
 * "Download diagnostic bundle", on one instance's screen (IM-54).
 *
 * ## A file, deliberately
 *
 * The point of a bundle is that it can be handed to Qrvey engineering without
 * handing them anything else. A file carries no session, no link back into
 * this console and no access to the customer's account — whoever receives it
 * can read it and can do nothing with it. A shareable URL would be the
 * opposite: something that grants what it shows.
 *
 * ## Offered only to the roles that hold the capability
 *
 * Computed from `capabilitiesFor` in `shared`, like every other offered
 * control here. Not a control: the API checks `diagnostics:bundle` and records
 * the decision either way. Offering a button that always fails is a worse way
 * to say no than not offering it.
 *
 * ## Collected when asked, never on render
 *
 * It is a handful of cross-account reads and an audit record naming the person
 * who asked, so it happens on the click and not on page load.
 */

/** Saves the document as a file, the way the audit export and the templates do. */
const save = (filename: string, document: unknown): void => {
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const canCollectBundle = (role: string): boolean =>
  isRole(role) && roleHasCapability(role, 'diagnostics:bundle')

export const DiagnosticBundleButton = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  role,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly role: string
}): JSX.Element | null => {
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<
    { readonly filename: string; readonly unreadable: readonly string[] } | undefined
  >(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  if (!canCollectBundle(role)) return null

  const collect = async (): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    setSaved(undefined)
    try {
      const bundle = await fetchDiagnosticBundle(baseUrl, tenantId, accountKey, deploymentId)
      const filename = diagnosticBundleFilename(deploymentId, bundle.collectedAt)
      save(filename, bundle)
      setSaved({ filename, unreadable: summarizeBundle(bundle).unreadable })
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 403
          ? 'Collecting a bundle needs diagnostics:bundle, which this role does not hold.'
          : 'Could not collect a bundle just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel">
      <h2>Diagnostics</h2>
      <p className="muted">
        One file with this instance’s runs, its account’s readiness in its region, the four stacks’
        recent events, its cluster and the tail of its logs — redacted, and carrying no access to
        the account, so it can be attached to a ticket or sent to Qrvey engineering.
      </p>
      <div className="actions">
        <button type="button" className="ghost" onClick={() => void collect()} disabled={busy}>
          {busy ? 'Collecting…' : 'Download diagnostic bundle'}
        </button>
      </div>
      {saved === undefined ? null : (
        <p role="status" className="muted">
          Saved <code>{saved.filename}</code>.
          {/* Said here as well as in the file: a section that could not be
            read is part of the answer, and whoever attaches the file should
            know before somebody else opens it. */}
          {saved.unreadable.length === 0
            ? null
            : ` Could not read: ${saved.unreadable.join(', ')} — the file says why.`}
        </p>
      )}
      {failure === undefined ? null : <p className="failure">{failure}</p>}
    </section>
  )
}
